import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, KeyRound, ShieldCheck, Sparkles } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import {
  AuthRequestError,
  resetPasswordRequest,
  type AuthValidationError,
} from '../../lib/api/auth-client';
import { AuthShell } from './auth-shell';
import {
  FORGOT_PASSWORD_PATH,
  LOGIN_PATH,
  resolveAuthenticatedPath,
} from './auth-routing';
import { useAuth } from './auth-context';

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(1, 'A palavra-passe é obrigatória.')
      .min(8, 'A palavra-passe deve ter pelo menos 8 caracteres.'),
    confirmPassword: z.string().min(1, 'Confirme a palavra-passe.'),
    rememberMe: z.boolean(),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'As palavras-passe têm de coincidir.',
    path: ['confirmPassword'],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

const resetHighlights = [
  {
    icon: KeyRound,
    title: 'Defina uma password segura',
    description:
      'Escolha uma nova password para recuperar o acesso com tranquilidade.',
  },
  {
    icon: ShieldCheck,
    title: 'Proteja a sua conta',
    description:
      'Evite repetir passwords antigas ou usadas noutros serviços.',
  },
  {
    icon: Sparkles,
    title: 'Continue de onde ficou',
    description:
      'Depois deste passo, pode voltar a entrar e seguir com o seu dia.',
  },
] as const;

const resetMetrics = [
  { value: '2 campos', label: 'Para concluir' },
  { value: '1 minuto', label: 'Em média' },
  { value: 'Acesso seguro', label: 'Com nova password' },
] as const;

const fieldClassName =
  'h-14 rounded-[1.35rem] border-[#dfd4c6] bg-[#fffdf9] text-[#16323b] shadow-[0_14px_28px_rgba(50,40,27,0.08)] placeholder:text-[#8c8578] focus-visible:ring-[#0f766e]/25 focus-visible:ring-offset-[#fffaf4]';

const labelClassName =
  'block text-left text-sm font-semibold uppercase tracking-[0.18em] text-[#6d665c]';

function getFieldErrorId(fieldName: keyof ResetPasswordFormValues) {
  return `${fieldName}-error`;
}

function getResetPasswordValidationMessage(validationError: AuthValidationError) {
  switch (validationError.field) {
    case 'password':
      if (validationError.errorType === 'blank') {
        return 'Indique a nova password.';
      }

      if (validationError.errorType === 'too_short') {
        return 'A nova password deve ter pelo menos 8 caracteres.';
      }

      return 'Introduza uma nova password válida.';
    case 'password_confirmation':
      return validationError.errorType === 'mismatch'
        ? 'As palavras-passe têm de coincidir.'
        : 'Confirme a nova password.';
    case 'reset_password_token':
      if (validationError.errorType === 'expired') {
        return 'O link de recuperação expirou. Peça um novo link.';
      }

      return 'O link de recuperação não é válido.';
    default:
      return null;
  }
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { acceptAuthSession, isAuthenticated, session } = useAuth();
  const resetPasswordToken =
    searchParams.get('reset_password_token')?.trim() ||
    searchParams.get('token')?.trim() ||
    '';

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
      rememberMe: false,
    },
  });

  const rememberMe = watch('rememberMe');

  React.useEffect(() => {
    register('rememberMe');
  }, [register]);

  React.useEffect(() => {
    if (isAuthenticated && session) {
      navigate(resolveAuthenticatedPath(session), { replace: true });
    }
  }, [isAuthenticated, navigate, session]);

  function handleRequestError(error: unknown) {
    if (error instanceof AuthRequestError && error.validationError) {
      const fieldMessage = getResetPasswordValidationMessage(error.validationError);

      if (fieldMessage && error.validationError.field === 'password') {
        setError('password', {
          type: 'server',
          message: fieldMessage,
        });
        return;
      }

      if (fieldMessage && error.validationError.field === 'password_confirmation') {
        setError('confirmPassword', {
          type: 'server',
          message: fieldMessage,
        });
        return;
      }

      if (fieldMessage) {
        setError('root.server', {
          type: 'server',
          message: fieldMessage,
        });
        return;
      }
    }

    setError('root.server', {
      type: 'server',
      message:
        error instanceof Error
          ? error.message
          : 'Não foi possível redefinir a password agora.',
    });
  }

  const onSubmit = handleSubmit(async (values) => {
    clearErrors('root');

    if (!resetPasswordToken) {
      setError('root.server', {
        type: 'server',
        message: 'O link de recuperação não é válido.',
      });
      return;
    }

    try {
      const response = await resetPasswordRequest({
        reset_password_token: resetPasswordToken,
        password: values.password,
        password_confirmation: values.confirmPassword,
      });
      const nextSession = await acceptAuthSession({
        response,
        rememberMe: values.rememberMe,
      });

      if (!nextSession) {
        navigate(LOGIN_PATH, {
          replace: true,
          state: {
            notice: 'Password redefinida com sucesso. Entre para continuar.',
          },
        });
        return;
      }

      navigate(resolveAuthenticatedPath(nextSession), { replace: true });
    } catch (error) {
      handleRequestError(error);
    }
  });

  if (!resetPasswordToken) {
    return (
      <AuthShell
        heroBadge="Link inválido"
        heroTitle="Este link já não pode ser usado."
        heroDescription="Este acesso de recuperação já não está disponível. Peça uma nova ligação para voltar a tentar com segurança."
        heroHighlights={resetHighlights}
        heroMetrics={resetMetrics}
        formBadge="Recuperação"
        formTitle="Link indisponível"
        formDescription="Pode iniciar novamente o processo de recuperação ou voltar ao login."
      >
        <div className="space-y-6">
          <div
            className="rounded-[1.5rem] border border-destructive/20 bg-destructive/10 px-4 py-4 text-sm font-medium text-destructive"
            role="alert"
          >
            O link de recuperação não é válido.
          </div>

          <Button
            type="button"
            size="lg"
            className="w-full rounded-[1.35rem] bg-[#0f766e] text-base shadow-[0_20px_40px_rgba(15,118,110,0.24)] hover:bg-[#0c615b]"
            onClick={() => navigate(FORGOT_PASSWORD_PATH, { replace: true })}
          >
            Pedir novo link
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full rounded-[1.35rem] border border-[#e5d8ca] bg-[#fff7ef] text-[1.02rem] text-[#16323b] hover:bg-[#f8efe3]"
            onClick={() => navigate(LOGIN_PATH, { replace: true })}
          >
            Voltar ao login
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      heroBadge="Nova password"
      heroTitle="Defina uma nova password e volte a entrar."
      heroDescription="Escolha uma nova password para voltar à sua conta com segurança e continuar o seu dia sem complicações."
      heroHighlights={resetHighlights}
      heroMetrics={resetMetrics}
      formBadge="Reset"
      formTitle="Redefinir password"
      formDescription="Escolha a nova password para esta conta e continue para a app."
    >
      <form className="space-y-6" onSubmit={onSubmit} noValidate>
        <div className="space-y-3">
          <label className={labelClassName} htmlFor="password">
            Nova password
          </label>
          <Input
            id="password"
            type="password"
            placeholder="Pelo menos 8 caracteres"
            autoComplete="new-password"
            className={cn(
              fieldClassName,
              errors.password && 'border-destructive/60 bg-destructive/5'
            )}
            aria-invalid={Boolean(errors.password)}
            aria-describedby={
              errors.password ? getFieldErrorId('password') : undefined
            }
            {...register('password', {
              onChange: () => {
                clearErrors('root.server');
              },
            })}
          />
          {errors.password ? (
            <p
              id={getFieldErrorId('password')}
              className="px-2 text-left text-sm font-medium text-destructive"
            >
              {errors.password.message}
            </p>
          ) : null}
        </div>

        <div className="space-y-3">
          <label className={labelClassName} htmlFor="confirmPassword">
            Confirmar palavra-passe
          </label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="Repita a palavra-passe"
            autoComplete="new-password"
            className={cn(
              fieldClassName,
              errors.confirmPassword && 'border-destructive/60 bg-destructive/5'
            )}
            aria-invalid={Boolean(errors.confirmPassword)}
            aria-describedby={
              errors.confirmPassword
                ? getFieldErrorId('confirmPassword')
                : undefined
            }
            {...register('confirmPassword', {
              onChange: () => {
                clearErrors('root.server');
              },
            })}
          />
          {errors.confirmPassword ? (
            <p
              id={getFieldErrorId('confirmPassword')}
              className="px-2 text-left text-sm font-medium text-destructive"
            >
              {errors.confirmPassword.message}
            </p>
          ) : null}
        </div>

        <div className="rounded-[1.5rem] border border-[#efe4d7] bg-[#fff7ef]/80 p-4 text-sm font-medium text-[#5f655f]">
          <label className="inline-flex cursor-pointer items-center gap-3">
            <Checkbox
              checked={rememberMe}
              onCheckedChange={(checked) =>
                setValue('rememberMe', checked === true, {
                  shouldDirty: true,
                })
              }
              aria-label="Manter sessão iniciada"
            />
            <span>Manter sessão iniciada</span>
          </label>
        </div>

        {errors.root?.server?.message ? (
          <div
            className="rounded-[1.5rem] border border-destructive/20 bg-destructive/10 px-4 py-4 text-sm font-medium text-destructive"
            role="alert"
          >
            {errors.root.server.message}
          </div>
        ) : null}

        <Button
          type="submit"
          size="lg"
          className="w-full rounded-[1.35rem] bg-[#0f766e] text-base shadow-[0_20px_40px_rgba(15,118,110,0.24)] hover:bg-[#0c615b]"
          disabled={isSubmitting}
        >
          <CheckCircle2 className="mr-2 h-5 w-5" />
          {isSubmitting ? 'A redefinir...' : 'Redefinir password'}
        </Button>
      </form>
    </AuthShell>
  );
}
